/**
 * Utilities for working with cookies.
 *
 * Loosely based on [client/cookie.js](https://github.com/optimizely/optimizely/blob/60249fa2a9cc2841978f12a19e0c3b239792920c/src/www/js/client/cookie.js) but some methods significantly refactored.
 * @module
 */

var _ = require('optimizely-client-fn');
var createError = require('./custom_error').create;
var DateEnv = require('../env/date');
var DocumentEnv = require('../env/document');
var WindowEnv = require('../env/window');

var actionTypes = require('../constants/action_types');
var di = require('../di');
var Flux = require('../flux');
var CookieOptionsStore = di.get('stores/cookie_options');

var SetError = exports.SetError = createError('CookieSetError');
var MismatchError = exports.MismatchError = createError('CookieMismatchError');

// SLDs stands for Second Level Domains
const OMITTED_SLDS = ['co', 'com', 'net', 'org', 'edu', 'gov', 'mil', 'int', 'ac', 'com', 'net', 'org'];

/**
 * Returns all cookies from the browser as a key/value object
 * Inspired by Angular's $$cookieReader
 *
 * @param {boolean} [decodeValue] Whether to automatically decode values before returning. Defaults to true.
 * @return {Object.<string,string>}
 */
exports.getAll = function(decodeValue?:boolean): Record<string, string> {
  if (_.isUndefined(decodeValue)) {
    decodeValue = true;
  }

  var cookieArray, cookie, i, index, name;

  cookieArray = DocumentEnv.getCookieString().split(/\s*;\s*/);
  var lastCookies = {} as any;

  for (i = 0; i < cookieArray.length; i++) {
    cookie = cookieArray[i];
    index = cookie.indexOf('=');
    if (index > 0) { //ignore nameless cookies
      name = exports.safeDecodeURIComponent(cookie.substring(0, index));
      // the first value that is seen for a cookie is the most
      // specific one.  values for the same cookie name that
      // follow are for less specific paths.
      if (lastCookies[name] === undefined) {
        var value = cookie.substring(index + 1);
        if (decodeValue) {
          value = exports.safeDecodeURIComponent(value);
        }
        lastCookies[name] = value;
      }
    }
  }
  return lastCookies;
};

/**
 * Try to decode string as if it's an encoded string. If it's malformed return the string.
 * @param {string} str
 * @returns {string}
 */
exports.safeDecodeURIComponent = function(str:string): string {
  try {
    return decodeURIComponent(str);
  } catch (e) {
    return str;
  }
};

/**
 * Get the value of a specific cookie by name.
 * @param {string} cookieName
 * @param {boolean} [decodeValue] Whether to automatically decode the value before returning. Defaults to true.
 * @returns {string|undefined}
 */
exports.get = function(cookieName:string, decodeValue?:boolean): string|undefined {
  var cookies = exports.getAll(decodeValue);
  return cookies[cookieName];
};

/**
 * Set a cookie with options.
 * @param {string} cookieName - cookie to set
 * @param {string} cookieValue - value to set, will be encoded with encodeURIComponent before storing
 * @param {CookieOptions} [options] - options to set as defined in https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie
 * NB - Expires overwrites maxAge if both are present. There is also no way to set a session cookie currently.
 * @param {boolean} [verify] - if true (default), check that the cookie was actually written, and throw if not.
 */
exports.set = function(cookieName:string, cookieValue:string, options?:CookieOptions, verify?:boolean) {
  options = _.extend({
      encodeValue: true
    },
    options
  );
  if (verify !== false) {
    verify = true;
  }

  if (__INVARIANT__) {
    require('./invariant').isType('CookieOptions', options);
  }

  var optionsString = [];

  if (_.isUndefined(options.domain)) {
    var currentDomain = CookieOptionsStore.getCurrentDomain();
    if (!currentDomain) {
      currentDomain = guessCookieDomain(WindowEnv.getLocation(), true);
    }
    options.domain = currentDomain;
  }
  if (options.domain) {
    optionsString.push('domain=' + options.domain);
  }

  if (_.isUndefined(options.path)) {
    options.path = '/';
  }

  if (options.path) {
    optionsString.push('path=' + options.path);
  }

  if (_.isUndefined(options.expires)) {
    var maxAge = _.isUndefined(options.maxAge) ? CookieOptionsStore.getDefaultAgeInSeconds() : options.maxAge;
    options.expires = new Date(DateEnv.now() + maxAge * 1000);
  }

  if (!_.isUndefined(options.expires)) {
    optionsString.push('expires=' + options.expires.toUTCString());
  }

  if (__SET_SECURE_CROSSSITE_COOKIES__) {
    optionsString.push('SameSite=None');
  }

  if (__SET_SECURE_CROSSSITE_COOKIES__ || options.secure) {
    optionsString.push('secure');
  }

  DocumentEnv.setCookie(cookieName + '=' + (options.encodeValue ? encodeURIComponent(cookieValue) : cookieValue) + ';' + optionsString.join(';'));

  if (verify) {
    // If we encoded the value to set it, we need to also decode it to test
    var decodeActualCookieValue = options.encodeValue;
    var actualCookieValue = exports.get(cookieName, decodeActualCookieValue);
    if (actualCookieValue !== cookieValue) {
      if (!actualCookieValue) {
        throw new SetError('Failed to set cookie "' + cookieName + '"');
      }

      throw new MismatchError('Expected "' + cookieValue + '" for "' + cookieName + '", got "' + actualCookieValue + '"');
    }
  }
};

/**
 * Remove cookie from all visible domains
 * @param {string} cookieName
 * @param {CookieOptions} options
 */
exports.remove = function(cookieName:string, options:CookieOptions) {
  var domainParts = WindowEnv.getLocation().hostname.split('.');

  // always omit last part of the domain unless domain is single string
  var limit = domainParts.length === 1 ? 0 : 1;

  while (domainParts.length > limit) {

    if (domainParts.length === 2 && _.includes(OMITTED_SLDS, domainParts[0])){
      // guessing the leftover are the SLD and TLD
      break;
    }

    exports.set(cookieName, null, _.extend({}, options, {
      domain: '.' + domainParts.join('.'),
      expires: new Date(0),
    }), false);
    domainParts.shift();
  }
};

/**
 * Get the highest possible domain where a cookie can be set, starting with the TLD. Store the result using Flux.
 *
 * @param {Location} location - current window.location
 * @param {boolean} [includeSubDomain] - whether to include the leading "." to make the cookie available to subdomains
 * @returns {string|null}
 * @private
 */
function guessCookieDomain(location:Location, includeSubDomain?:boolean): string|null {
  if (includeSubDomain !== false) {
    includeSubDomain = true;
  }

  var parts = location.hostname.split('.');
  var domainParts = [];
  var domainString;
  var options;
  var result = null;

  for (var i = parts.length - 1; i >= 0; i--) {
    domainParts.unshift(parts[i]);
    domainString = domainParts.join('.');

    if (parts.length > 1 && (
      domainParts.length === 1 ||
      domainParts.length === 2 && _.includes(OMITTED_SLDS, domainParts[0])
    )) {
      // Skip all TLDs and basic SLDs
      continue;
    }

    if (_.includes(BLACKLIST_DOMAINS, domainString)) {
      continue;
    }

    options = {
      domain: includeSubDomain ? ('.' + domainString) : domainString,
    };

    try {
      exports.set(DOMAIN_TEST_COOKIE_NAME, Math.random().toString(), options);
      exports.remove(DOMAIN_TEST_COOKIE_NAME, options);
      // success
      result = options.domain;
      break;
    } catch(err) {} // eslint-disable-line
  }

  Flux.dispatch(actionTypes.SET_COOKIE_DOMAIN, result);
  return result;
}

/**
 * Domains on which we will NEVER set cookies. Check if it's a "TLD" first: https://publicsuffix.org/list/effective_tld_names.dat
 * @type {string[]}
 */
var BLACKLIST_DOMAINS = [
  // This is useful for local testing since we use optimizely.test as a TLD locally.
  'optimizely.test',
];

/**
 *
 * @type {string}
 */
var DOMAIN_TEST_COOKIE_NAME = 'optimizelyDomainTestCookie';

if (__TEST__) {
  exports.guessCookieDomain = guessCookieDomain;
  exports.TEST_COOKIE_NAME = DOMAIN_TEST_COOKIE_NAME;
}

